import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionsAPI } from '../../api/subscriptions';
import { formatCurrency } from '../../utils/formatters';
import { NotificationContext } from '../../context/NotificationContext';
import Button from '../common/Button';
import Modal from '../common/Modal';
import SubscriptionPlanForm from './SubscriptionPlanForm';
import './SubscriptionManagement.css';

// ─── Constants ────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'all',      label: 'All Plans',  icon: '📋', filter: null },
  { id: 'active',   label: 'Active',     icon: '✅', filter: 'active' },
  { id: 'inactive', label: 'Inactive',   icon: '⏸️', filter: 'inactive' },
];

const PLAN_TYPE_META = {
  credit: { label: 'Credit', bg: '#eef2ff', color: '#3730a3' },
  pieces: { label: 'Pieces', bg: '#ecfeff', color: '#155e75' },
};

// ─── Inline components ────────────────────────────────────────────────────────
const SummaryCard = ({ icon, label, value, sub, accent }) => (
  <div className="ps-summary-card" style={{ borderTopColor: accent }}>
    <div className="ps-summary-icon">{icon}</div>
    <div>
      <div className="ps-summary-value" style={{ color: accent }}>{value}</div>
      <div className="ps-summary-label">{label}</div>
      {sub && <div className="ps-summary-sub">{sub}</div>}
    </div>
  </div>
);

const formatPlanReceives = (plan) => {
  if (plan.planType === 'credit') return formatCurrency(plan.creditAmount || 0);
  if (plan.planType === 'pieces') return `${plan.pieceLimit || 0} pieces`;
  return '—';
};

const formatPlanValidity = (plan) =>
  plan.hasExpiry ? `${plan.durationDays} day${plan.durationDays === 1 ? '' : 's'}` : 'Open-ended';

// ─── Main Component ───────────────────────────────────────────────────────────
const PlanManagement = () => {
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext);

  const [activeTab, setActiveTab]   = useState('all');
  const [plans, setPlans]           = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading]       = useState(false);

  const [summary, setSummary]       = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editTarget, setEditTarget]       = useState(null);

  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateTarget, setDeactivateTarget]       = useState(null);
  const [deactivateLoading, setDeactivateLoading]     = useState(false);

  const loadSummary = useCallback(async () => {
    try {
      const res = await subscriptionsAPI.getPlansSummary();
      setSummary(res.data);
    } catch { /* non-critical */ }
  }, []);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      const tabFilter = TABS.find(t => t.id === activeTab)?.filter;
      if (tabFilter === 'active') params.isActive = 'true';
      else if (tabFilter === 'inactive') params.isActive = 'false';

      const res = await subscriptionsAPI.getPlans(params);
      setPlans(res.data);
      setTotalCount(res.data.length);
    } catch {
      showNotification('Failed to load plans', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { loadSummary(); }, []);
  useEffect(() => { loadPlans(); }, [activeTab]);

  const handleCreate = async (data) => {
    try {
      await subscriptionsAPI.createPlan(data);
      showNotification('Plan created successfully', 'success');
      setShowCreateModal(false);
      loadPlans();
      loadSummary();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to create plan', 'error');
    }
  };

  const openEditModal = (plan, e) => {
    e?.stopPropagation();
    setEditTarget(plan);
    setShowEditModal(true);
  };

  const handleEdit = async (data) => {
    try {
      await subscriptionsAPI.updatePlan(editTarget._id, data);
      showNotification('Plan updated successfully', 'success');
      setShowEditModal(false);
      setEditTarget(null);
      loadPlans();
      loadSummary();
      if (selectedPlan?._id === editTarget._id) {
        const res = await subscriptionsAPI.getPlanById(editTarget._id);
        setSelectedPlan(res.data);
      }
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to update plan', 'error');
    }
  };

  const handleDeactivate = async () => {
    setDeactivateLoading(true);
    try {
      await subscriptionsAPI.updatePlan(deactivateTarget._id, { isActive: false });
      showNotification('Plan deactivated', 'success');
      setShowDeactivateModal(false);
      setDeactivateTarget(null);
      if (selectedPlan?._id === deactivateTarget._id) setSelectedPlan(null);
      loadPlans();
      loadSummary();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to deactivate plan', 'error');
    } finally {
      setDeactivateLoading(false);
    }
  };

  const tabCount = (filterId) => {
    if (!summary || !filterId) return null;
    if (filterId === 'active') return summary.activeCount;
    if (filterId === 'inactive') return summary.inactiveCount;
    return null;
  };

  return (
    <div className="ps-root">

      <div className="ps-header">
        <div>
          <h1 className="ps-title">Subscription Plans</h1>
          <p className="ps-subtitle">Define prepaid plans the cashier can sell at the till</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={() => navigate('/subscriptions')}>
            ← Back to Subscriptions
          </Button>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            + New Plan
          </Button>
        </div>
      </div>

      {summary && (
        <div className="ps-summary-row">
          <SummaryCard
            icon="📋" label="Total Plans" accent="#3b82f6"
            value={summary.totalCount ?? 0}
            sub={`${summary.activeCount ?? 0} active`}
          />
          <SummaryCard
            icon="👥" label="Active Subscribers" accent="#10b981"
            value={summary.totalActiveSubscriptions ?? 0}
            sub="across all plans"
          />
          <SummaryCard
            icon="💰" label="Lifetime Revenue" accent="#8b5cf6"
            value={formatCurrency(summary.totalRevenue ?? 0)}
            sub="from prepaid plans"
          />
          <SummaryCard
            icon="🪙" label="Credit Plans" accent="#6366f1"
            value={summary.creditPlans ?? 0}
            sub="amount-based"
          />
          <SummaryCard
            icon="👕" label="Pieces Plans" accent="#06b6d4"
            value={summary.piecesPlans ?? 0}
            sub="usage-based"
          />
        </div>
      )}

      <div className="ps-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`ps-tab${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="ps-tab-icon">{tab.icon}</span>
            {tab.label}
            {tab.filter && tabCount(tab.filter) != null && (
              <span className="om-tab-count">{tabCount(tab.filter)}</span>
            )}
          </button>
        ))}
      </div>

      <div className="ps-content">
        <div className="ps-tab-pane">

          <div className="ps-table-meta">
            <span className="ps-count">{totalCount} plan{totalCount !== 1 ? 's' : ''}</span>
          </div>

          {loading ? (
            <div className="ps-loading">Loading plans…</div>
          ) : (
            <div className="ps-table-wrap">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th>Plan Name</th>
                    <th>Type</th>
                    <th>Price</th>
                    <th>Customer Receives</th>
                    <th>Validity</th>
                    <th>Subscribers</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {plans.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="ps-empty">
                        <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
                        No plans found
                      </td>
                    </tr>
                  ) : plans.map(plan => {
                    const typeMeta = PLAN_TYPE_META[plan.planType] || { label: plan.planType, bg: '#f3f4f6', color: '#374151' };
                    return (
                      <tr key={plan._id} className="ps-row" onClick={() => setSelectedPlan(plan)}>
                        <td>
                          <div style={{ fontWeight: 700, color: '#6366f1', fontSize: 14 }}>{plan.name}</div>
                          {plan.description && (
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{plan.description}</div>
                          )}
                        </td>
                        <td>
                          <span className="om-badge" style={{ background: typeMeta.bg, color: typeMeta.color }}>
                            {typeMeta.label}
                          </span>
                        </td>
                        <td style={{ fontWeight: 800, color: '#0f172a', fontSize: 15 }}>
                          {formatCurrency(plan.price)}
                        </td>
                        <td style={{ fontWeight: 700, color: '#0f172a' }}>
                          {formatPlanReceives(plan)}
                          {plan.planType === 'credit' && plan.creditAmount > plan.price && (
                            <div style={{ fontSize: 11, color: '#10b981', marginTop: 2, fontWeight: 600 }}>
                              +{formatCurrency(plan.creditAmount - plan.price)} bonus
                            </div>
                          )}
                        </td>
                        <td style={{ color: '#64748b' }}>{formatPlanValidity(plan)}</td>
                        <td style={{ fontWeight: 600, color: '#374151' }}>{plan.activeSubscribers ?? 0}</td>
                        <td>
                          <span className="om-badge" style={plan.isActive
                            ? { background: '#d1fae5', color: '#065f46' }
                            : { background: '#fee2e2', color: '#991b1b' }
                          }>
                            {plan.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="om-action-btn" onClick={e => openEditModal(plan, e)} title="Edit plan">
                              ✏️
                            </button>
                            {plan.isActive && (
                              <button
                                className="om-action-btn danger"
                                onClick={e => { e.stopPropagation(); setDeactivateTarget(plan); setShowDeactivateModal(true); }}
                                title="Deactivate"
                              >
                                ⏸️
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Plan Detail */}
      {selectedPlan && (
        <Modal
          isOpen={!!selectedPlan}
          onClose={() => setSelectedPlan(null)}
          title={selectedPlan.name}
          size="large"
        >
          <PlanDetailContent plan={selectedPlan} onEdit={openEditModal} />
        </Modal>
      )}

      {/* Create */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="New Subscription Plan"
      >
        <SubscriptionPlanForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreateModal(false)}
        />
      </Modal>

      {/* Edit */}
      <Modal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setEditTarget(null); }}
        title="Edit Plan"
      >
        {editTarget && (
          <SubscriptionPlanForm
            plan={editTarget}
            onSubmit={handleEdit}
            onCancel={() => { setShowEditModal(false); setEditTarget(null); }}
          />
        )}
      </Modal>

      {/* Deactivate */}
      <Modal
        isOpen={showDeactivateModal}
        onClose={() => { setShowDeactivateModal(false); setDeactivateTarget(null); }}
        title="Deactivate Plan"
      >
        {deactivateTarget && (
          <div className="ps-modal-form">
            <div className="om-delete-warning">
              <span style={{ fontSize: 32 }}>⚠️</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>
                  Deactivate "{deactivateTarget.name}"?
                </div>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  The plan will no longer appear at checkout. Customers already subscribed remain active until their balance or expiry runs out.
                </div>
              </div>
            </div>
            <div className="ps-modal-actions">
              <Button variant="secondary" onClick={() => { setShowDeactivateModal(false); setDeactivateTarget(null); }}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDeactivate} disabled={deactivateLoading}>
                {deactivateLoading ? 'Deactivating…' : 'Deactivate Plan'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ─── Plan Detail Content ──────────────────────────────────────────────────────
const PlanDetailContent = ({ plan, onEdit }) => {
  const typeMeta = PLAN_TYPE_META[plan.planType] || { label: plan.planType, bg: '#f3f4f6', color: '#374151' };

  return (
    <div className="pm-detail">

      <div className="om-detail-top-bar">
        <div className="om-detail-badges">
          <span className="om-badge" style={plan.isActive
            ? { background: '#d1fae5', color: '#065f46' }
            : { background: '#fee2e2', color: '#991b1b' }
          }>
            {plan.isActive ? 'Active' : 'Inactive'}
          </span>
          <span className="om-badge" style={{ background: typeMeta.bg, color: typeMeta.color }}>
            {typeMeta.label} Plan
          </span>
          <span className="om-badge" style={{ background: '#f1f5f9', color: '#0f172a' }}>
            {formatPlanValidity(plan)}
          </span>
        </div>
        <button className="ps-settle-btn-lg" onClick={(e) => onEdit(plan, e)}>Edit Plan</button>
      </div>

      <div className="pm-detail-grid">
        <div className="pm-plan-card">
          <div className="pm-plan-name">{plan.name}</div>
          {plan.description && <div className="pm-plan-desc">{plan.description}</div>}
          <div className="pm-plan-price">{formatCurrency(plan.price)}</div>
          <div className="pm-plan-duration">{formatPlanValidity(plan)}</div>

          <div className="pm-plan-divider" />

          <div className="pm-benefits-list">
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, opacity: 0.9 }}>
              Customer receives
            </div>
            {plan.planType === 'credit' ? (
              <>
                <div className="pm-benefit-item">
                  <span className="pm-benefit-item-icon">🪙</span>
                  <span>{formatCurrency(plan.creditAmount)} usable credit</span>
                </div>
                {plan.creditAmount > plan.price && (
                  <div className="pm-benefit-item">
                    <span className="pm-benefit-item-icon">🎁</span>
                    <span>{formatCurrency(plan.creditAmount - plan.price)} bonus value</span>
                  </div>
                )}
              </>
            ) : (
              <div className="pm-benefit-item">
                <span className="pm-benefit-item-icon">👕</span>
                <span>{plan.pieceLimit} pieces of laundry covered</span>
              </div>
            )}
            <div className="pm-benefit-item">
              <span className="pm-benefit-item-icon">📅</span>
              <span>{plan.hasExpiry ? `Valid for ${plan.durationDays} day(s)` : 'No expiry'}</span>
            </div>
          </div>
        </div>

        <div className="om-detail-section">
          <div className="om-section-title">Plan Statistics</div>
          <div className="pm-stats-grid">
            <div className="pm-stat-box">
              <div className="pm-stat-value">{plan.activeSubscribers ?? 0}</div>
              <div className="pm-stat-label">Active Subscribers</div>
            </div>
            <div className="pm-stat-box">
              <div className="pm-stat-value">{formatCurrency((plan.activeSubscribers ?? 0) * plan.price)}</div>
              <div className="pm-stat-label">Revenue (active)</div>
            </div>
          </div>

          {plan.planType === 'credit' && plan.creditAmount > plan.price && (
            <div style={{
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              borderRadius: 10, padding: '12px 14px', marginTop: 12,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#065f46', textTransform: 'uppercase', marginBottom: 4 }}>
                Margin
              </div>
              <div style={{ fontSize: 12, color: '#166534', lineHeight: 1.5 }}>
                The plan grants <strong>{formatCurrency(plan.creditAmount - plan.price)}</strong> bonus credit per sale —
                roughly <strong>{((1 - plan.price / plan.creditAmount) * 100).toFixed(1)}%</strong> off list price for the customer.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanManagement;
